import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export interface NavModule {
  slug: string;
  name: string;
  icon: string | null;
  routePath: string;
  navGroup: string | null;
  sortOrder: number;
}

/** Menú dinámico (arquitectura modular multi-industria): módulos habilitados para la organización del usuario, ya filtrados por sus permisos reales. */
export function useMyNav() {
  return useQuery({
    queryKey: ["modules", "my-nav"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<NavModule[]>>("/modules/my-nav");
      return data.data;
    },
    staleTime: 5 * 60_000,
  });
}

export interface IndustryRecord {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
}

export function useIndustries(enabled: boolean) {
  return useQuery({
    queryKey: ["modules", "industries"],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<IndustryRecord[]>>("/modules/industries");
      return data.data;
    },
  });
}

export interface OrganizationModuleRow {
  moduleId: string;
  slug: string;
  name: string;
  category: string;
  navGroup: string | null;
  enabled: boolean;
}

export function useOrganizationModules(organizationId: string | null) {
  return useQuery({
    queryKey: ["modules", "organization", organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<OrganizationModuleRow[]>>(`/modules/organizations/${organizationId}`);
      return data.data;
    },
  });
}

export function useSetOrganizationModules(organizationId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: Array<{ moduleId: string; enabled: boolean }>) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<OrganizationModuleRow[]>>(`/modules/organizations/${organizationId}`, { updates });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["modules", "organization", organizationId] }),
  });
}
